import { toast } from '@/components/ui/use-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';

import {
  createClub,
  updateClub,
  uploadImageForClub,
} from '@/api/clubs/clubApi';
import { Club, ClubAddress } from '@/types/club.model';
import { PlusCircle, TrashIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { GoogleMapContainer } from './GoogleMapContainer';
import { ImageUploader } from '@/components/common/ImageUploader';
import { generateTimeRange } from './utils';
import {
  ClubFormData,
  dayOfWeekEnum,
  dayOfWeekMap,
  schema,
} from './validations';
import { handleGlobalError } from '@/utils/error-handler';
import { handleCuitInput } from '@/utils/cuit-utils';

interface UpsertClubProps {
  club?: Club;
  isReadOnly?: boolean;
}

const timeRange = generateTimeRange();
const defaultStartTime = '07:00';
const defaultEndTime = '23:00';

export const UpsertClub: React.FC<UpsertClubProps> = ({
  club,
  isReadOnly = false,
}) => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    trigger,
    formState: { errors, isValid, isSubmitting },
  } = useForm<ClubFormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: club
      ? {
          name: club.name,
          cuit: club.cuit,
          email: club.email,
          cellphone: club.cellphone,
          instagramLink: club.instagramLink,
          facebookLink: club.facebookLink,
          bannerUrl: club.bannerUrl,
          address: club.address,
          availability: club.availability,
          status: club.status,
        }
      : undefined,
  });

  const [isUploading, setIsUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'availability',
  });

  const onSubmit = async (data: ClubFormData) => {
    if (isReadOnly) return;
    return club?.id ? update(data) : create(data);
  };

  const create = async (data: ClubFormData) => {
    try {
      await createClub(data);
      toast({
        title: 'Club creado',
        description: 'El club se ha creado correctamente',
        variant: 'default',
      });
      navigate('/clubs');
    } catch (error) {
      handleGlobalError(
        error,
        'No se pudo crear el club',
        'Hubo un error al intentar crear este club.',
      );
    }
  };

  const update = async (data: ClubFormData) => {
    try {
      await updateClub({ ...data, id: club?.id });
      toast({
        title: 'Club Actualizado',
        description: 'El club se ha actualizado correctamente',
        variant: 'default',
      });
      navigate('/clubs');
    } catch (error) {
      handleGlobalError(
        error,
        'No se pudo actualizar el club',
        'Hubo un error al intentar actualizar este club.',
      );
    }
  };

  const appendAvailability = () => {
    if (isReadOnly) return;

    const selectedDays = watch('availability').map((item) => item.dayOfWeek);

    const availableDays = Object.keys(dayOfWeekMap).filter(
      (day) => !selectedDays.includes(day as z.infer<typeof dayOfWeekEnum>),
    );

    if (availableDays.length === 0) {
      return;
    }

    append({
      date: null,
      dayOfWeek: availableDays[0] as z.infer<typeof dayOfWeekEnum>,
      timeRange: [{ startTime: '07:00', endTime: '23:00' }],
    });
  };

  const availability = watch('availability');

  const handleOnAddressUpdate = useCallback(
    (address: ClubAddress | null) => {
      if (isReadOnly) return;
      setValue(
        'address',
        { ...address!, timezone: 'America/Argentina/Buenos_Aires' }, // TODO: get timezone from address ticket #764
        {
          shouldValidate: true,
          shouldDirty: true,
        },
      );
    },
    [setValue, isReadOnly],
  );

  const normalizeAddress = club
    ? `${club.address.street}, ${club.address.city}, ${club.address.state}, ${club.address.country.name}`
    : '';

  const initialized = useRef(false);

  const handleFileChange = async (file: File) => {
    if (isReadOnly) return;

    setIsUploading(true);
    try {
      const result = await uploadImageForClub(file);
      if (result) {
        setValue('bannerUrl', result, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setImagePreview(result);
        toast({
          title: 'Imagen subida',
          description: 'La imagen del banner se ha subido exitosamente.',
        });
      } else {
        throw new Error('Upload failed');
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      handleGlobalError(
        error,
        'Error',
        'Hubo un problema al subir la imagen. Por favor, intente nuevamente.',
      );
    } finally {
      setIsUploading(false);
    }
  };

  const displayImagePreview = imagePreview || club?.bannerUrl || null;

  useEffect(() => {
    if (!initialized.current && club) {
      initialized.current = true;

      handleOnAddressUpdate({
        ...club.address,
        fullCoordinates: `${club.address.latitude},${club.address.longitude}`,
      });

      const availability = club.availability.map((availability) => ({
        ...availability,
        date: null,
        timeRange: availability.timeRange.map((time) => {
          const [startH, startM] = time.startTime.split(':');
          const [endH, endM] = time.endTime.split(':');
          return {
            ...time,
            startTime: `${startH}:${startM}`,
            endTime: `${endH}:${endM}`,
          };
        }),
      }));

      if (availability.length > 0) {
        setValue('availability', availability as ClubFormData['availability'], {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
  }, [club, handleOnAddressUpdate, setValue]);

  return (
    <div className={isReadOnly ? 'opacity-90' : ''}>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <div className="-mx-3 mb-6 mt-4 flex flex-wrap">
          <div className="mb-6 w-full px-3 md:mb-0 md:w-1/2">
            <Label
              className="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-700"
              htmlFor="grid-first-name"
            >
              Nombre del club
            </Label>
            <Input
              {...register('name')}
              className={`mb-3 block w-full appearance-none rounded border ${errors.name ? 'border-red-500' : 'border-gray-300'} px-4 py-3 leading-tight text-gray-700 focus:bg-white focus:outline-none ${
                isReadOnly ? 'cursor-not-allowed bg-gray-100' : ''
              }`}
              id="grid-first-name"
              type="text"
              placeholder="Nombre del club"
              readOnly={isReadOnly}
            />
            {errors.name && (
              <p className="text-xs italic text-red-500">
                {errors.name.message}
              </p>
            )}
          </div>
        </div>

        <div className="-mx-3 mb-6 mt-4 flex flex-wrap">
          <div className="mb-6 w-full px-3 md:mb-0 md:w-1/2">
            <Label
              className="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-700"
              htmlFor="grid-cuit"
            >
              Cuit del club
            </Label>
            <Input
              {...register('cuit')}
              className={`mb-3 block w-full appearance-none rounded border ${errors.cuit ? 'border-red-500' : 'border-gray-300'} px-4 py-3 leading-tight text-gray-700 focus:bg-white focus:outline-none ${
                isReadOnly ? 'cursor-not-allowed bg-gray-100' : ''
              }`}
              id="grid-cuit"
              type="text"
              placeholder="Cuit del club"
              readOnly={isReadOnly}
              maxLength={11}
              onInput={handleCuitInput}
            />
            {errors.cuit && (
              <p className="text-xs italic text-red-500">
                {errors.cuit.message}
              </p>
            )}
          </div>
          <div className="mb-6 w-full px-3 md:mb-0 md:w-1/2">
            <Label
              className="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-700"
              htmlFor="grid-email"
            >
              Email
            </Label>
            <Input
              {...register('email')}
              className={`mb-3 block w-full appearance-none rounded border ${errors.email ? 'border-red-500' : 'border-gray-300'} px-4 py-3 leading-tight text-gray-700 focus:bg-white focus:outline-none ${
                isReadOnly ? 'cursor-not-allowed bg-gray-100' : ''
              }`}
              id="grid-email"
              type="text"
              placeholder="Email del club"
              readOnly={isReadOnly}
            />
            {errors.email && (
              <p className="text-xs italic text-red-500">
                {errors.email.message}
              </p>
            )}
          </div>
        </div>

        <div className="-mx-3 mb-6 mt-4 flex flex-wrap">
          <div className="mb-6 w-full px-3 md:mb-0 md:w-1/3">
            <Label
              className="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-700"
              htmlFor="grid-phone"
            >
              Teléfono
            </Label>
            <Input
              {...register('cellphone')}
              className={`mb-3 block w-full appearance-none rounded border ${errors.cellphone ? 'border-red-500' : 'border-gray-300'} px-4 py-3 leading-tight text-gray-700 focus:bg-white focus:outline-none ${
                isReadOnly ? 'cursor-not-allowed bg-gray-100' : ''
              }`}
              id="grid-phone"
              type="text"
              placeholder="Teléfono del club"
              readOnly={isReadOnly}
            />
            {errors.cellphone && (
              <p className="text-xs italic text-red-500">
                {errors.cellphone.message}
              </p>
            )}
          </div>
          <div className="mb-6 w-full px-3 md:mb-0 md:w-1/3">
            <Label
              className="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-700"
              htmlFor="grid-instagram"
            >
              Instagram
            </Label>
            <Input
              {...register('instagramLink')}
              className={`mb-3 block w-full appearance-none rounded border ${errors.instagramLink ? 'border-red-500' : 'border-gray-300'} px-4 py-3 leading-tight text-gray-700 focus:bg-white focus:outline-none ${
                isReadOnly ? 'cursor-not-allowed bg-gray-100' : ''
              }`}
              id="grid-instagram"
              type="text"
              placeholder="Instagram del club"
              readOnly={isReadOnly}
            />
            {errors.instagramLink && (
              <p className="text-xs italic text-red-500">
                {errors.instagramLink.message}
              </p>
            )}
          </div>
          <div className="mb-6 w-full px-3 md:mb-0 md:w-1/3">
            <Label
              className="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-700"
              htmlFor="grid-facebook"
            >
              Facebook
            </Label>
            <Input
              {...register('facebookLink')}
              className={`mb-3 block w-full appearance-none rounded border ${errors.facebookLink ? 'border-red-500' : 'border-gray-300'} px-4 py-3 leading-tight text-gray-700 focus:bg-white focus:outline-none ${
                isReadOnly ? 'cursor-not-allowed bg-gray-100' : ''
              }`}
              id="grid-facebook"
              type="text"
              placeholder="Facebook del club"
              readOnly={isReadOnly}
            />
            {errors.facebookLink && (
              <p className="text-xs italic text-red-500">
                {errors.facebookLink.message}
              </p>
            )}
          </div>
        </div>

        <div
          className={`mt-8 rounded-md border p-4 ${isReadOnly ? 'bg-gray-50' : ''}`}
        >
          <h3 className="mb-4 font-semibold">Imagen del club</h3>
          {isReadOnly ? (
            <div className="text-center">
              {displayImagePreview ? (
                <img
                  src={displayImagePreview}
                  alt="Banner del club"
                  className="mx-auto max-h-64 rounded-md object-cover"
                />
              ) : (
                <div className="flex h-32 items-center justify-center rounded-md bg-gray-200 text-gray-500">
                  Sin imagen
                </div>
              )}
            </div>
          ) : (
            <ImageUploader
              onChange={handleFileChange}
              isUploading={isUploading}
              imagePreview={displayImagePreview}
              altText="Previsualización del banner del club"
            />
          )}
        </div>

        <div
          className={`mt-8 rounded-md border p-4 ${isReadOnly ? 'bg-gray-50' : ''}`}
        >
          <h3 className="mb-4 font-semibold">Dirección del club</h3>
          <GoogleMapContainer
            onAddressUpdate={handleOnAddressUpdate}
            initial={normalizeAddress}
            isReadOnly={isReadOnly}
          />
        </div>

        <div
          className={`mt-8 rounded-md border p-4 ${isReadOnly ? 'bg-gray-50' : ''}`}
        >
          <h3 className="mb-2 font-semibold">Disponibilidad</h3>

          {errors.availability && (
            <p className="my-4 text-sm text-red-500">
              {errors.availability.message}
            </p>
          )}

          {fields.map((field, index) => (
            <div key={field.id} className="mb-2 flex items-center space-x-2">
              <Controller
                control={control}
                name={`availability.${index}.dayOfWeek`}
                render={({ field }) => (
                  <Select
                    onValueChange={(value) => {
                      if (!isReadOnly) {
                        field.onChange(value);
                        trigger('availability');
                      }
                    }}
                    defaultValue={field.value}
                    value={field.value}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger
                      className={`w-32 ${isReadOnly ? 'cursor-not-allowed bg-gray-100' : ''}`}
                    >
                      <SelectValue placeholder="Día" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(dayOfWeekMap).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />

              <Controller
                control={control}
                name={`availability.${index}.timeRange.0.startTime`}
                defaultValue={defaultStartTime}
                render={({ field }) => (
                  <Select
                    onValueChange={isReadOnly ? undefined : field.onChange}
                    defaultValue={field.value || defaultStartTime}
                    value={field.value || defaultStartTime}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger
                      className={`w-32 ${isReadOnly ? 'cursor-not-allowed bg-gray-100' : ''}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {timeRange.map((hour) => (
                        <SelectItem key={hour} value={hour}>
                          {hour}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />

              <span>-</span>

              <Controller
                control={control}
                name={`availability.${index}.timeRange.0.endTime`}
                defaultValue={defaultEndTime}
                render={({ field }) => (
                  <Select
                    onValueChange={isReadOnly ? undefined : field.onChange}
                    defaultValue={field.value || defaultEndTime}
                    value={field.value || defaultEndTime}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger
                      className={`w-32 ${isReadOnly ? 'cursor-not-allowed bg-gray-100' : ''}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {timeRange.map((hour) => (
                        <SelectItem key={hour} value={hour}>
                          {hour}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />

              {!isReadOnly && (
                <Button
                  type="button"
                  variant={'destructive'}
                  onClick={() => remove(index)}
                  className="h-9"
                >
                  <TrashIcon size={'20px'}></TrashIcon>
                </Button>
              )}
            </div>
          ))}

          {!isReadOnly && (
            <Button
              type="button"
              className="mt-4 flex gap-2"
              onClick={appendAvailability}
              disabled={
                (!!errors.availability && availability.length > 1) ||
                availability?.length >= 7
              }
            >
              <PlusCircle size={18} />
              <span>Agregar disponibilidad</span>
            </Button>
          )}
        </div>

        {!isReadOnly && (
          <Button
            type="submit"
            variant={'default'}
            className="mt-8 w-full px-6 py-4 text-xl shadow-md"
            disabled={!isValid || isSubmitting}
          >
            {club ? 'Actualizar' : 'Crear Club'}
          </Button>
        )}
      </form>
    </div>
  );
};
